import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useBaseCurrency } from "@/hooks/use-base-currency";
import { cn } from "@/lib/utils";
import { AutoBreadcrumb } from "@/components/ui/auto-breadcrumb";

// Feature hooks
import { usePortfolioData } from "../hooks/usePortfolioData";
import { useTWRData } from "../hooks/useTWRData";
import { useBenchmark } from "../hooks/useBenchmark";
import { useMaxTWRData } from "../hooks/useMaxTWRData";
import { usePortfolioAssets } from "../hooks/usePortfolioAssets";
import { useCurrentPerformance } from "../hooks/useCurrentPerformance";
import { useCurrentValuation } from "../hooks/useCurrentValuation";
import { usePortfolioRecordCount } from "../hooks/usePortfolioRecordCount";
import { portfolioKeys } from "../hooks/queries";
import { mergeCurrentValuationIntoPerformance } from "../utils/current-valuation-utils";
import {
  getPortfolioTabConfig,
  hasPortfolioHoldings,
  isPortfolioTab,
  resolvePortfolioTab,
} from "../utils/portfolio-tabs";

// Feature components
import { PortfolioHeader } from "./PortfolioHeader";
import { PortfolioKeyMetrics } from "./PortfolioKeyMetrics";
import { PortfolioValueChart } from "./ValueChart/PortfolioValueChart";
import { TWRChartSection } from "./TWRChart/TWRChartSection";
import { AssetAllocationChart } from "./AssetAllocationChart";
import { CountryAllocationChart } from "./CountryAllocationChart";
import { SectorAllocationChart } from "./SectorAllocationChart";
import { IndustryAllocationChart } from "./IndustryAllocationChart";
import { DividendHistogram } from "./DividendHistogram";
import { UpcomingEventsCard } from "./UpcomingEventsCard";
import { PositionsTableContainer } from "./PositionsSection/PositionsTable";
import { PortfolioTransactionsTab } from "./PortfolioTransactionsTab";
import { ManagePortfoliosModal } from "./ManagePortfoliosModal";
import { AllPortfoliosBreakdown } from "./AllPortfoliosBreakdown";
import { CreatePortfolioDialog } from "./CreatePortfolioDialog";
import { CashTable, getCashPositions } from "./CashTable";

export default function PortfolioPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const search = useSearch({ strict: false }) as { tab?: string; bookId?: string };

  // Local UI state
  const [period, setPeriod] = useState<string>("max");
  const [isCreatePortfolioOpen, setIsCreatePortfolioOpen] = useState(false);
  const [isManagePortfoliosOpen, setIsManagePortfoliosOpen] = useState(false);
  const { code: currencyCode } = useBaseCurrency();

  const selectedBookId = search.bookId ?? "global";
  const requestedTab = isPortfolioTab(search.tab) ? search.tab : undefined;

  const handleTabChange = (nextTab: string) => {
    const safeTab = resolvePortfolioTab(nextTab, allowedTabs);

    void navigate({
      to: "/portfolio",
      search: (previous) => ({
        ...previous,
        tab: safeTab,
      }),
    });
  };

  const handleBookChange = (bookId: string) => {
    void navigate({
      to: "/portfolio",
      search: (previous) => ({
        ...previous,
        bookId: bookId === "global" ? undefined : bookId,
      }),
    });
  };

  const handlePortfolioCreated = (bookId: string) => {
    void navigate({
      to: "/portfolio",
      search: (previous) => ({
        ...previous,
        tab: "transactions",
        bookId: bookId === "global" ? undefined : bookId,
      }),
    });
  };

  const refreshPortfolioQueries = async () => {
    await queryClient.invalidateQueries({ queryKey: portfolioKeys.all });
  };

  // Data fetching hooks
  const {
    books,
    chartData,
    performanceData,
    loading: portfolioLoading,
  } = usePortfolioData(selectedBookId, period);

  // Current performance snapshot (doesn't change with period)
  const {
    performanceData: currentPerformance,
    loading: currentPerformanceLoading,
  } = useCurrentPerformance(selectedBookId);
  const {
    currentValuation,
    loading: currentValuationLoading,
  } = useCurrentValuation(selectedBookId);
  const { recordCount, loading: recordCountLoading } =
    usePortfolioRecordCount(selectedBookId);

  const twr = useTWRData(selectedBookId, period);
  const mergedPeriodPerformance = mergeCurrentValuationIntoPerformance(
    performanceData,
    currentValuation,
  );
  const mergedCurrentPerformance = mergeCurrentValuationIntoPerformance(
    currentPerformance,
    currentValuation,
  );

  const benchmark = useBenchmark(twr.displayTwrChartData, twr.fetchPeriod);

  // Always fetch max TWR for all-time CAGR
  const maxTwr = useMaxTWRData(selectedBookId);

  // Fetch assets in bulk for allocation charts (based on current holdings)
  const { assetsMap, loading: assetsLoading } = usePortfolioAssets(
    mergedCurrentPerformance?.data ?? null,
  );
  const selectedBook = books.find((book) => book.book_id === selectedBookId);
  const shouldDelayEmptyState =
    selectedBookId !== "global" &&
    selectedBook?.book_type !== "cash" &&
    (currentPerformanceLoading || currentValuationLoading || recordCountLoading);
  const isEmptyPortfolio =
    !shouldDelayEmptyState &&
    selectedBookId !== "global" &&
    !hasPortfolioHoldings({
      currentPerformance,
      currentValuation,
    }) &&
    recordCount === 0;
  const { allowedTabs, isCashAccount } = useMemo(
    () =>
      getPortfolioTabConfig({
        selectedBookId,
        bookType: selectedBook?.book_type,
        isEmpty: isEmptyPortfolio,
      }),
    [isEmptyPortfolio, selectedBook?.book_type, selectedBookId],
  );
  const activeTab = resolvePortfolioTab(requestedTab, allowedTabs);
  const cashPositions = useMemo(
    () => getCashPositions(mergedCurrentPerformance, currencyCode),
    [currencyCode, mergedCurrentPerformance],
  );

  useEffect(() => {
    const shouldPreserveImplicitDefault =
      search.tab === undefined &&
      activeTab === "dashboard" &&
      allowedTabs.length === 4;

    if (search.tab === activeTab || shouldPreserveImplicitDefault) {
      return;
    }

    void navigate({
      to: "/portfolio",
      replace: true,
      search: (previous) => ({
        ...previous,
        tab: activeTab,
      }),
    });
  }, [activeTab, navigate, search.tab]);

  return (
    <div className="h-full w-full flex flex-col p-3 md:p-6 gap-4 md:gap-6 overflow-auto">
      {/* Top Bar - Breadcrumb + Book Selector */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 flex-shrink-0">
        <AutoBreadcrumb />
        <PortfolioHeader
          books={books}
          selectedBookId={selectedBookId}
          onBookChange={handleBookChange}
          onCreatePortfolio={() => setIsCreatePortfolioOpen(true)}
          onManagePortfolios={() => setIsManagePortfoliosOpen(true)}
          loading={portfolioLoading.books}
        />
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={handleTabChange} className="flex-1 flex flex-col">
        <TabsList variant="line" className="mb-4">
          {allowedTabs.includes("dashboard") && (
            <TabsTrigger value="dashboard" className="text-xs sm:text-sm">Dashboard</TabsTrigger>
          )}
          {allowedTabs.includes("positions") && (
            <TabsTrigger value="positions" className="text-xs sm:text-sm">Positions</TabsTrigger>
          )}
          {allowedTabs.includes("diversification") && (
            <TabsTrigger value="diversification" className="text-xs sm:text-sm">
              <span className="hidden lg:inline">Diversification</span>
              <span className="lg:hidden">Diversif.</span>
            </TabsTrigger>
          )}
          {allowedTabs.includes("transactions") && (
            <TabsTrigger value="transactions" className="text-xs sm:text-sm">Transactions</TabsTrigger>
          )}
        </TabsList>

        {/* Dashboard Tab */}
        {allowedTabs.includes("dashboard") && (
          <TabsContent
            value="dashboard"
            className="flex-1 flex flex-col gap-4 md:gap-6 mt-0 overflow-auto"
          >
          {/* Key Metrics Section - All-Time Statistics */}
          <PortfolioKeyMetrics
            maxTwrData={maxTwr.maxTwrData}
            performanceData={currentPerformance}
            currentValuation={currentValuation}
            currencyCode={currencyCode}
            variant={isCashAccount ? "cash" : "default"}
            loading={{
              twr: maxTwr.loading,
              performance: currentPerformanceLoading || currentValuationLoading,
            }}
          />

          {/* Performance Charts + Dividends/Events (same row) */}
          <div className={cn("w-full grid grid-cols-1 gap-4 md:gap-6", !isCashAccount && "lg:grid-cols-3")}>
            <div className={cn(
              "rounded-2xl border border-border/40 bg-card shadow-sm p-3 sm:p-4 md:p-6 flex flex-col min-h-[340px] sm:min-h-[400px]",
              !isCashAccount && "lg:col-span-2",
            )}>
              <Tabs defaultValue="value" className="flex-1 flex flex-col">
                <TabsList variant="line" className="self-start mb-4 flex-shrink-0">
                  <TabsTrigger value="value" className="text-xs sm:text-sm">Portfolio Value</TabsTrigger>
                  <TabsTrigger value="twr" className="text-xs sm:text-sm">TWR</TabsTrigger>
                </TabsList>

                <TabsContent value="value" className="flex-1 mt-0">
                  <PortfolioValueChart
                    chartData={chartData}
                    performanceData={mergedPeriodPerformance}
                    currentValuation={currentValuation}
                    period={period}
                    onPeriodChange={setPeriod}
                    currencyCode={currencyCode}
                    loading={{
                      chart: portfolioLoading.chart,
                      performance:
                        portfolioLoading.performance || currentValuationLoading,
                    }}
                  />
                </TabsContent>

                <TabsContent value="twr" className="flex-1 mt-0">
                  <TWRChartSection
                    mergedChartData={benchmark.mergedChartData}
                    yAxisDomain={benchmark.yAxisDomain}
                    formatTwrXAxis={twr.formatTwrXAxis}
                    loading={twr.loading}
                    lastTwr={twr.lastTwr}
                    mode={twr.mode}
                    annualizedReturn={twr.annualizedReturn}
                    period={period}
                    onPeriodChange={setPeriod}
                    rangeEnabled={twr.rangeEnabled}
                    range={twr.range}
                    formattedRange={twr.formattedRange}
                    rangePopoverOpen={twr.rangePopoverOpen}
                    onRangePopoverOpenChange={twr.setRangePopoverOpen}
                    onRangeEnableChange={twr.setRangeEnabled}
                    onRangeChange={twr.setRange}
                    benchmarkEnabled={benchmark.enabled}
                    onBenchmarkEnabledChange={benchmark.setEnabled}
                    benchmarkAsset={benchmark.asset}
                    onBenchmarkAssetChange={benchmark.setAsset}
                    benchmarkData={benchmark.alignedData}
                    benchmarkLoading={benchmark.loading}
                    lastBenchmarkReturn={benchmark.lastBenchmarkReturn}
                  />
                </TabsContent>
              </Tabs>
            </div>

            {!isCashAccount && (
              <div className="flex flex-col gap-4 md:gap-6 h-full">
                <DividendHistogram
                  bookId={selectedBookId}
                  currencyCode={currencyCode}
                  className="flex-1 min-h-0"
                  contentClassName="flex-1 min-h-0 h-full"
                />
                <UpcomingEventsCard
                  performanceData={mergedCurrentPerformance?.data ?? null}
                  className="flex-1 min-h-0"
                />
              </div>
            )}
          </div>

          {isCashAccount && cashPositions.length > 0 && (
            <div className="bg-card rounded-2xl border border-border/40 shadow-sm overflow-hidden">
              <CashTable
                cashPositions={cashPositions}
                baseCurrencyCode={currencyCode}
                headerLabel="Cash Balances"
                showTopBorder={false}
              />
            </div>
          )}

          {selectedBookId === "global" && (
            <AllPortfoliosBreakdown
              books={books}
              currencyCode={currencyCode}
              onSelectPortfolio={handleBookChange}
              selectedBookId={selectedBookId}
            />
          )}
          </TabsContent>
        )}

        {/* Positions Tab */}
        {allowedTabs.includes("positions") && (
          <TabsContent value="positions" className="flex-1 mt-0 overflow-hidden">
          <PositionsTableContainer
            currentPerformance={currentPerformance}
            currentValuation={currentValuation}
            selectedBookId={selectedBookId}
            currencyCode={currencyCode}
            loadingBase={currentPerformanceLoading || currentValuationLoading}
          />
          </TabsContent>
        )}

        {/* Diversification Tab */}
        {allowedTabs.includes("diversification") && (
          <TabsContent
            value="diversification"
            className="flex-1 flex flex-col gap-4 md:gap-6 mt-0 overflow-auto"
          >
          {/* Grid: Asset Allocation + Sector Allocation side by side */}
          <div className="w-full grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Left - Asset Allocation */}
            <div className="bg-card rounded-2xl border border-border/40 shadow-sm p-3 sm:p-4 md:p-6 flex flex-col min-h-[350px] sm:min-h-[300px]">
              {currentPerformanceLoading || currentValuationLoading ? (
                <Skeleton className="h-full w-full rounded-xl" />
              ) : (
                <AssetAllocationChart
                  performanceData={mergedCurrentPerformance?.data ?? null}
                  className="h-full w-full"
                />
              )}
            </div>

            {/* Right - Sector/Industry/Country Allocation (Tabbed) */}
            <div className="bg-card rounded-2xl border border-border/40 shadow-sm p-4 md:p-6 flex flex-col min-h-[400px]">
              {currentPerformanceLoading || currentValuationLoading || assetsLoading ? (
                <Skeleton className="h-full w-full rounded-xl" />
              ) : (
                <Tabs defaultValue="sector" className="h-full flex flex-col">
                  <TabsList variant="line" className="self-start mb-3">
                    <TabsTrigger value="sector" className="text-xs">
                      Sector
                    </TabsTrigger>
                    <TabsTrigger value="industry" className="text-xs">
                      Industry
                    </TabsTrigger>
                    <TabsTrigger value="country" className="text-xs">
                      Country
                    </TabsTrigger>
                  </TabsList>
                  <TabsContent value="sector" className="flex-1 mt-0">
                    <SectorAllocationChart
                      performanceData={mergedCurrentPerformance?.data ?? null}
                      assetsMap={assetsMap}
                      loading={assetsLoading}
                      className="h-full w-full"
                    />
                  </TabsContent>
                  <TabsContent value="industry" className="flex-1 mt-0">
                    <IndustryAllocationChart
                      performanceData={mergedCurrentPerformance?.data ?? null}
                      assetsMap={assetsMap}
                      loading={assetsLoading}
                      className="h-full w-full"
                    />
                  </TabsContent>
                  <TabsContent value="country" className="flex-1 mt-0">
                    <CountryAllocationChart
                      performanceData={mergedCurrentPerformance?.data ?? null}
                      assetsMap={assetsMap}
                      loading={assetsLoading}
                      className="h-full w-full"
                    />
                  </TabsContent>
                </Tabs>
              )}
            </div>
          </div>
          </TabsContent>
        )}

        {allowedTabs.includes("transactions") && (
          <TabsContent value="transactions" className="flex-1 mt-0 overflow-auto">
            <PortfolioTransactionsTab selectedBookId={selectedBookId} books={books} />
          </TabsContent>
        )}
      </Tabs>

      <ManagePortfoliosModal
        open={isManagePortfoliosOpen}
        onOpenChange={setIsManagePortfoliosOpen}
        books={books}
        selectedBookId={selectedBookId}
        onBookSelect={handleBookChange}
        onCreatePortfolio={() => setIsCreatePortfolioOpen(true)}
        onPortfoliosChanged={refreshPortfolioQueries}
      />
      <CreatePortfolioDialog
        open={isCreatePortfolioOpen}
        onOpenChange={setIsCreatePortfolioOpen}
        onCreated={handlePortfolioCreated}
        onPortfoliosChanged={refreshPortfolioQueries}
      />
    </div>
  );
}
